import { isBootColdEntryPath } from "@/lib/bootColdEntry";
import { isPublicBlogPath } from "@/lib/posts/blogPostRoute";
import { isPublicVisualReadyPath } from "@/lib/publicMarketingBoot";

const AUTH_SURFACE_PATHS = new Set([
  "/auth",
  "/login",
  "/register",
  "/reset-password",
  "/auth/callback",
]);

/** Auth routes that must reveal even when a session already exists. */
const AUTH_SURFACES_SIGNAL_READY_WHEN_AUTHENTICATED = new Set([
  "/reset-password",
]);

const REDIRECT_ENTRY_PATHS = new Set([
  "/",
  "/aramlas",
  "/chat",
  "/auth/callback",
]);

const stripQueryAndHash = (value: string) => value.split(/[?#]/, 1)[0] || value;

export const getRevealTargetKey = (value: string) => value;

export const getRevealPathname = (value: string) => stripQueryAndHash(value);

export const isAuthSurfacePath = (pathname: string) => AUTH_SURFACE_PATHS.has(stripQueryAndHash(pathname));

export const isRedirectEntryPath = (pathname: string) => REDIRECT_ENTRY_PATHS.has(stripQueryAndHash(pathname));

export const routeRequiresTopChrome = (pathname: string) => {
  return false;
};

export const shouldSignalRouteReady = (pathname: string, hasAuthenticatedUser = false) => {
  const normalized = stripQueryAndHash(pathname);

  if (isRedirectEntryPath(normalized)) {
    return false;
  }

  if (isPublicVisualReadyPath(normalized)) {
    return false;
  }

  if (
    isAuthSurfacePath(normalized) &&
    hasAuthenticatedUser &&
    !AUTH_SURFACES_SIGNAL_READY_WHEN_AUTHENTICATED.has(normalized)
  ) {
    return false;
  }

  return true;
};

export const shouldBypassEntitlementGate = (pathname: string) => {
  const normalized = stripQueryAndHash(pathname);
  return (
    isBootColdEntryPath(normalized) ||
    isAuthSurfacePath(normalized) ||
    isPublicBlogPath(normalized)
  );
};

const ROUTE_DEFERRED_ENTITLEMENT_GATE_PATHS = new Set([
  "/",
  "/archivum",
  "/archivum/checkout",
  "/aramlas/home",
]);

const REVEAL_TARGET_ALIASES: Record<string, readonly string[]> = {
  "/aramlas": ["/aramlas/home", "/aramlas/nincs-hozzaferes"],
  "/": ["/start"],
  "/home": ["/start"],
};

export const isRevealTargetAlias = (activeKey: string, signalKey: string): boolean => {
  const normalizedActive = stripQueryAndHash(activeKey);
  const normalizedSignal = stripQueryAndHash(signalKey);

  if (normalizedActive === normalizedSignal) {
    return true;
  }

  return REVEAL_TARGET_ALIASES[normalizedActive]?.includes(normalizedSignal) ?? false;
};

const PROTOTYPE_ROUTE_PREFIX = "/terv/";

/** Routes that render their own in-layout loading UI inside PublicLayout / ArramlasLayout. */
export const shouldDeferEntitlementGateToRoute = (pathname: string) => {
  const normalized = stripQueryAndHash(pathname);
  return (
    ROUTE_DEFERRED_ENTITLEMENT_GATE_PATHS.has(normalized) ||
    normalized.startsWith(PROTOTYPE_ROUTE_PREFIX)
  );
};
